import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import {
  green,
  blue,
  amber,
  orange,
  red,
  grey,
} from '@material-ui/core/colors';
import { formatDate } from 'share/utils';
import {
  IvrCallStatusSummary,
  IvrSurveyPhase,
} from 'modules/patients/interfaces';

const PHASE_LABEL: Record<IvrSurveyPhase, string> = {
  [IvrSurveyPhase.Dialing]: 'Dialing',
  [IvrSurveyPhase.InCall]: 'In call',
  [IvrSurveyPhase.Completed]: 'Completed',
  [IvrSurveyPhase.Partial]: 'Partial',
  [IvrSurveyPhase.RetryScheduled]: 'Retry scheduled',
  [IvrSurveyPhase.Unreachable]: 'Unreachable',
  [IvrSurveyPhase.OptedOut]: 'Opted out',
};

const PHASE_COLOR: Record<IvrSurveyPhase, string> = {
  [IvrSurveyPhase.Dialing]: blue[400],
  [IvrSurveyPhase.InCall]: blue[700],
  [IvrSurveyPhase.Completed]: green[700],
  [IvrSurveyPhase.Partial]: amber[800],
  [IvrSurveyPhase.RetryScheduled]: orange[700],
  [IvrSurveyPhase.Unreachable]: red[700],
  [IvrSurveyPhase.OptedOut]: grey[800],
};

/** Colour for a phase, so callers can tint the row to match. */
export const ivrPhaseColor = (phase?: IvrSurveyPhase | null) =>
  phase ? PHASE_COLOR[phase] : undefined;

export const ivrPhaseLabel = (phase?: IvrSurveyPhase | null) =>
  phase ? PHASE_LABEL[phase] : undefined;

type Props = {
  call: IvrCallStatusSummary;
};

/**
 * Phone-survey progress, inline on a survey result.
 *
 * `description` is written server-side and stays correct as the backend
 * evolves, so it is shown as-is rather than recomposed from the parts.
 */
export const IvrPhaseChip: React.FC<Props> = ({ call }) => {
  const details = useMemo(() => {
    const lines: string[] = [];

    if (typeof call.answeredCount === 'number' && call.answeredCount > 0) {
      lines.push(`${call.answeredCount} answered`);
    }
    // attemptCount counts call status transitions, not dials -- one call
    // produces several -- so it is labelled activity rather than attempts.
    if (typeof call.attemptCount === 'number' && call.attemptCount > 0) {
      lines.push(`${call.attemptCount} call events`);
    }
    if (typeof call.retriesRemaining === 'number') {
      lines.push(`${call.retriesRemaining} attempt(s) remaining`);
    }
    if (call.nextRetryAt) {
      lines.push(`Next attempt ${formatDate(call.nextRetryAt, true, true)}`);
    }
    if (call.lastActivityAt) {
      lines.push(`Updated ${formatDate(call.lastActivityAt, true, true)}`);
    }
    return lines;
  }, [call]);

  const title = (
    <Box>
      <Typography variant="caption" component="div">
        {call.description}
      </Typography>
      {details.map(line => (
        <Typography key={line} variant="caption" component="div">
          {line}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Tooltip title={title} arrow>
      <Box
        component="span"
        ml={1}
        px={0.75}
        style={{
          backgroundColor: 'rgba(255,255,255,0.25)',
          border: '1px solid rgba(255,255,255,0.55)',
          borderRadius: 3,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          lineHeight: '16px',
          display: 'inline-block',
        }}
      >
        {PHASE_LABEL[call.phase]}
        {typeof call.answeredCount === 'number' && call.answeredCount > 0
          ? ` · ${call.answeredCount}`
          : ''}
      </Box>
    </Tooltip>
  );
};

export default React.memo(IvrPhaseChip);
